import { call, put, select, takeEvery } from 'redux-saga/effects';
import { push } from 'react-router-redux';
import { types } from '@actions/conference';
import { cleanConferences } from '@actions/conferences';
import { cleanTranscripts } from '@actions/transcripts';
import { getTransciptEntryById } from '@services/transcriptEntryResource';
import {
  validateConference,
  upsertConference,
  getConferenceById,
  regeneratePin,
  transformConferenceResponse,
  requestHumanTranscript,
} from '@services/conferenceResource';
import { tokenSelector, otpSelector } from '@selectors/authentication';
import { selectConference, selectConferenceId } from '@selectors/conference';
import {
  blockTwoFactorAuthForm,
  unblockTwoFactorAuthForm,
  attemptsCountExhausted,
} from '@actions/authentication';
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from '@config/api-messages';
import _ from 'lodash';

export function* createConferenceSaga(action) {
  try {
    const token = yield select(tokenSelector);
    const conference = yield select(selectConference);
    validateConference(conference);

    yield call(upsertConference, conference, token);
    yield put({
      type: types.CREATE_CONFERENCE.SUCCESS,
      message: SUCCESS_MESSAGES.CONFERENCE_CREATED,
    });
    yield put(cleanConferences());
    yield put(cleanTranscripts());
    yield put(push('/'));
  } catch (err) {
    yield put({
      type: types.CREATE_CONFERENCE.FAILURE,
      message: err.message || ERROR_MESSAGES.CANT_CREATING_TRANSCRIPT,
      errors: err.errors,
    });
  }
}

export function* getById(resource, action) {
  try {
    const token = yield select(tokenSelector);
    const otp = yield select(otpSelector);
    const conferenceResponse = yield call(resource, action, token, otp);
    const conference = transformConferenceResponse(
      conferenceResponse.data.transcript,
      true,
    );
    yield put({
      type: types.GET_BY_ID.SUCCESS,
      conference,
    });
  } catch (err) {
    const attemptsCountReached =
      _.get(err, 'response.data.error.name') === ERROR_MESSAGES.ACCOUNT_BLOCKED;

    if (attemptsCountReached) {
      yield put(blockTwoFactorAuthForm());
      yield put(attemptsCountExhausted());
    } else {
      yield put(unblockTwoFactorAuthForm());
    }
    yield put({
      type: types.GET_BY_ID.FAILURE,
      message:
        _.get(err, 'response.data.error.message') ||
        err.message ||
        ERROR_MESSAGES.CANT_FETCH_CONFERENCE,
    });
    // Error 403 stands for two-factor auth restriction.
    if (err.response && err.response.status === 403) {
      const type = action.isAIRoute ? '&type=ai-route' : '';
      yield put(push(`/two-factor-auth?id=${action.id}${type}`));
    }
    if (err.response && err.response.status === 401) {
      yield put(push('/no-permission'));
    }
  }
}

export function* conferenceGetByIdSaga(action) {
  yield call(getById, getConferenceById, action);
}

export function* transcriptEntryGetByIdSaga(action) {
  yield call(getById, getTransciptEntryById, action);
}

export function* updateConferenceSaga(action) {
  try {
    const token = yield select(tokenSelector);
    const conference = yield select(selectConference);
    validateConference(conference);
    yield call(upsertConference, conference, token);
    yield put({
      type: types.UPDATE_CONFERENCE.SUCCESS,
      message: SUCCESS_MESSAGES.CONFERENCE_UPDATED,
    });
    yield put({
      type: types.GET_BY_ID.STARTED,
      id: conference.id,
    });
    if (!conference.edited) yield put(push('/')); // conference view page behaviour
  } catch (err) {
    yield put({
      type: types.UPDATE_CONFERENCE.FAILURE,
      message: err.message || ERROR_MESSAGES.CANT_UPDATE_CONFERENCE,
      errors: err.errors,
    });
  }
}

export function* regeneratePinSaga(action) {
  try {
    const token = yield select(tokenSelector);
    const conferenceId = yield select(selectConferenceId);
    const { response } = (yield call(regeneratePin, conferenceId, token)).data;
    yield put({
      type: types.REGENERATE_PIN.SUCCESS,
      ...response,
      message: SUCCESS_MESSAGES.PIN_GENERATED,
    });
  } catch (err) {
    yield put({
      type: types.REGENERATE_PIN.FAILURE,
      message: err.message || ERROR_MESSAGES.ERROR_GENERATING_PIN,
    });
  }
}

export function* requestHumanTranscriptSaga(action) {
  try {
    const token = yield select(tokenSelector);
    const conferenceId = action.conferenceId
      ? action.conferenceId
      : yield select(selectConferenceId);
    yield call(requestHumanTranscript, conferenceId, token);
    yield put({
      type: types.REQUEST_HUMAN_TRANSCRIPT.SUCCESS,
      message: SUCCESS_MESSAGES.HUMAN_TRANSCRIPT_REQUESTED,
    });
  } catch (err) {
    yield put({
      type: types.REQUEST_HUMAN_TRANSCRIPT.FAILURE,
      message: err.message || ERROR_MESSAGES.CANT_REQUEST_HUMAN_TRANSCRIPT,
    });
  }
}

export default function*() {
  yield takeEvery(types.CREATE_CONFERENCE.STARTED, createConferenceSaga);
  yield takeEvery(types.GET_BY_ID.STARTED, conferenceGetByIdSaga);
  yield takeEvery(types.UPDATE_CONFERENCE.STARTED, updateConferenceSaga);
  yield takeEvery(types.REGENERATE_PIN.STARTED, regeneratePinSaga);
  yield takeEvery(types.GET_TRANSCRIPT_ENTRY_BY_ID, transcriptEntryGetByIdSaga);
  yield takeEvery(types.REQUEST_HUMAN_TRANSCRIPT.STARTED, requestHumanTranscriptSaga);
}



// WEBPACK FOOTER //
// ./redux/sagas/conference.js